import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

const STOP_BENCH_REASONS = [
	{ value: "new_assignment", label: "New assignment found" },
	{ value: "hired_elsewhere", label: "Hired elsewhere" },
	{ value: "contract_ended", label: "Contract ended" },
	{ value: "resigned", label: "Consultant resigned" },
	{ value: "no_longer_available", label: "No longer available" },
	{ value: "other", label: "Other" },
] as const;

interface StopBenchModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	candidateName: string;
	onConfirm: (data: { reason: string; note?: string }) => void;
	isLoading?: boolean;
}

export const StopBenchModal = ({
	open,
	onOpenChange,
	candidateName,
	onConfirm,
	isLoading,
}: StopBenchModalProps) => {
	const { t } = useTranslation();
	const [reason, setReason] = useState("");
	const [note, setNote] = useState("");

	const handleConfirm = () => {
		if (!reason) return;
		onConfirm({ reason, note: note.trim() || undefined });
		setReason("");
		setNote("");
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			setReason("");
			setNote("");
		}
		onOpenChange(open);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>
						{t("workforce.bench.stopBenchTitle", "End Bench Period")}
					</DialogTitle>
					<DialogDescription>
						{t("workforce.bench.stopBenchDesc", "End the bench period for {{name}}. Today's date will be used as the end date.", { name: candidateName })}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label>{t("workforce.bench.stopReason", "Reason")} *</Label>
						<Select value={reason} onValueChange={setReason}>
							<SelectTrigger>
								<SelectValue placeholder={t("workforce.bench.selectStopReason", "Select reason...")} />
							</SelectTrigger>
							<SelectContent>
								{STOP_BENCH_REASONS.map((r) => (
									<SelectItem key={r.value} value={r.value}>
										{t(`workforce.bench.stopReasons.${r.value}`, r.label)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-2">
						<Label>{t("workforce.bench.stopNote", "Note (optional)")}</Label>
						<Textarea
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder={t("workforce.bench.stopNotePlaceholder", "Any additional context...")}
							rows={3}
							className="resize-none"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button onClick={handleConfirm} disabled={!reason || isLoading}>
						{t("workforce.bench.confirmStop", "End Bench")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
